// components/GlobalSearch.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { SearchNormal1 } from "iconsax-reactjs";
import { debounce } from "lodash";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const GlobalSearch = () => {
  const { isDarkMode } = useTheme();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);

  const performSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length < 2) {
        setResults(null);
        setError(null);
        setLoading(false);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      setLoading(true);
      setError(null);

      try {
        const response = await apiService.search(searchQuery, {
          signal: abortControllerRef.current.signal,
          entities: ["Lead", "Account", "Contact", "Opportunity"], // Limit initial search
          limit: 20,
        });

        if (response.success !== false) {
          setResults(response.data || response);
          setShowResults(true);
        } else {
          setError(response.message || "Search failed");
        }
      } catch (error) {
        // Ignore abort errors
        if (error.name === "AbortError" || error.name === "CanceledError") {
          return;
        }

        // Handle timeout specifically
        if (error.code === "ECONNABORTED") {
          setError("Search took too long. Try a more specific query.");
        } else if (error.response?.status === 500) {
          setError("Server error. Please try again.");
        } else {
          console.error("Search error:", error);
          setError("Search failed. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    }, 400),
    [],
  );

  useEffect(() => {
    performSearch(query);

    return () => {
      performSearch.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, performSearch]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowResults(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleResultClick = (result) => {
    window.location.href = result.url;
    setShowResults(false);
    setQuery("");
  };

  const handleClearSearch = () => {
    setQuery("");
    setResults(null);
    setShowResults(false);
    setError(null);
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full max-w-2xl">
      <div className="relative">
        <SearchNormal1
          className={`absolute left-3 top-1/2 -translate-y-1/2 ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
          size={20}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results && setShowResults(true)}
          placeholder="Search leads, accounts, contacts..."
          className={`w-full pl-10 pr-10 py-2 border rounded-lg placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors ${
            isDarkMode
              ? "bg-[#1A1D24] border-[#22262F] text-white"
              : "bg-white border-gray-300 text-gray-900"
          }`}
        />
        {query && !loading && (
          <button
            onClick={handleClearSearch}
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-xl leading-none ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {error && showResults && (
        <div
          className={`absolute top-full mt-2 w-full rounded-lg p-3 text-sm z-50 ${
            isDarkMode
              ? "bg-red-500/10 border border-red-500/20 text-red-400"
              : "bg-red-50 border border-red-200 text-red-600"
          }`}
        >
          {error}
        </div>
      )}

      {showResults && results && !error && (
        <div
          className={`absolute top-full mt-2 w-full rounded-lg shadow-xl max-h-96 overflow-y-auto z-50 border ${
            isDarkMode
              ? "bg-[#1A1D24] border-[#22262F]"
              : "bg-white border-gray-200"
          }`}
        >
          <div
            className={`p-3 border-b sticky top-0 ${
              isDarkMode
                ? "bg-[#1A1D24] border-[#22262F]"
                : "bg-white border-gray-200"
            }`}
          >
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Found {results.totalResults} result
              {results.totalResults !== 1 ? "s" : ""}
              {results.totalResults > 0 && (
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  {' for "'}
                  {results.query}""
                </span>
              )}
            </p>
          </div>

          {results.totalResults === 0 ? (
            <div
              className={`p-6 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              <p>No results found for "{results.query}"</p>
              <p
                className={`text-sm mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                Try a different search term
              </p>
            </div>
          ) : (
            Object.entries(results.resultsByEntity).map(([entity, items]) => (
              <div
                key={entity}
                className={`border-b last:border-b-0 ${
                  isDarkMode ? "border-[#22262F]" : "border-gray-200"
                }`}
              >
                <div
                  className={`px-3 py-2 sticky top-12 ${
                    isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {entity}
                    <span
                      className={`ml-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      ({results.facets[entity]})
                    </span>
                  </p>
                </div>
                {items.map((result) => (
                  <div
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className={`px-3 py-3 cursor-pointer transition-colors ${
                      isDarkMode ? "hover:bg-[#22262F]" : "hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-medium truncate ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {result.title}
                        </p>
                        {result.subtitle && (
                          <p
                            className={`text-sm mt-1 truncate ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {result.subtitle}
                          </p>
                        )}
                        {result.metadata.owner && (
                          <p
                            className={`text-xs mt-1 ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Owner: {result.metadata.owner}
                          </p>
                        )}
                      </div>
                      {result.metadata.status && (
                        <span
                          className={`px-2 py-1 text-xs rounded flex-shrink-0 capitalize ${
                            isDarkMode
                              ? "bg-blue-500/20 text-blue-400"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {result.metadata.status.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {showResults && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowResults(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default GlobalSearch;
